import { Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AuthService } from '../../services/auth.service';

interface CrmActivityRow {
  id: number; customerId: number; contactId: number | null; caseId: number | null;
  activityType: string; subject: string; body: string | null;
  occurredAt: string; isSystem: boolean;
  createdByUserId: number | null; createdByUserName: string | null; createdAt: string;
  followUpOn: string | null; followUpTime: string | null; followUpCompletedAt: string | null;
}
interface CrmActivityPage { items: CrmActivityRow[]; total: number; page: number; pageSize: number; }

/**
 * Append-only CRM interaction timeline, embeddable against a customer and,
 * optionally, a specific case (case worklog). Composer + reverse-chronological
 * history + per-activity follow-up scheduling and completion.
 */
@Component({
  selector: 'app-activity-timeline',
  templateUrl: './activity-timeline.component.html',
  styleUrls: ['./activity-timeline.component.css'],
})
export class ActivityTimelineComponent implements OnChanges, OnDestroy {
  @Input() customerId!: number;
  @Input() caseId: number | null = null;

  readonly activityTypes = ['Call', 'Email', 'Meeting', 'Demo', 'SiteVisit', 'Note'];
  activities: CrmActivityRow[] = [];
  activitiesLoading = false;
  activitiesLoaded = false;
  activityPage = 1;
  activityTotal = 0;
  private readonly activityPageSize = 25;
  loggingActivity = false;
  activityError = '';
  activityForm: { activityType: string; subject: string; body: string; followUpOn: string; followUpTime: string } =
    { activityType: 'Note', subject: '', body: '', followUpOn: '', followUpTime: '' };

  private readonly destroyed$ = new Subject<void>();

  constructor(private http: HttpClient, private snackBar: MatSnackBar, public auth: AuthService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['customerId'] || changes['caseId']) && this.customerId) {
      this.loadActivities(true);
    }
  }

  ngOnDestroy(): void { this.destroyed$.next(); this.destroyed$.complete(); }

  private url(page: number): string {
    let u = `/api/activities?customerId=${this.customerId}&page=${page}&pageSize=${this.activityPageSize}`;
    if (this.caseId) u += `&caseId=${this.caseId}`;
    return u;
  }

  loadActivities(reset = false): void {
    if (!this.customerId) return;
    if (reset) { this.activities = []; this.activityPage = 1; this.activitiesLoaded = false; }
    if (this.activitiesLoaded && !reset) return;
    if (this.activitiesLoading) return;
    this.activitiesLoading = true;
    this.http.get<CrmActivityPage>(this.url(this.activityPage)).pipe(takeUntil(this.destroyed$)).subscribe({
      next: p => {
        this.activities = this.activityPage === 1 ? p.items : [...this.activities, ...p.items];
        this.activityTotal = p.total;
        this.activitiesLoading = false;
        this.activitiesLoaded = true;
      },
      error: () => { this.activitiesLoading = false; },
    });
  }

  loadMoreActivities(): void {
    if (this.activities.length >= this.activityTotal || this.activitiesLoading) return;
    this.activityPage += 1;
    this.activitiesLoaded = false;
    this.loadActivities();
  }

  logActivity(): void {
    const subject = this.activityForm.subject.trim();
    if (!subject) { this.activityError = 'Subject is required.'; return; }
    this.loggingActivity = true;
    this.activityError = '';
    const payload = {
      customerId: this.customerId,
      caseId: this.caseId,
      activityType: this.activityForm.activityType,
      subject,
      body: this.activityForm.body.trim() || null,
      followUpOn: this.activityForm.followUpOn || null,
      // A time only means something with a date; send 'HH:mm:ss' for TimeOnly.
      followUpTime: this.activityForm.followUpOn && this.activityForm.followUpTime
        ? this.activityForm.followUpTime + ':00' : null,
    };
    this.http.post('/api/activities', payload).pipe(takeUntil(this.destroyed$)).subscribe({
      next: () => {
        this.loggingActivity = false;
        this.activityForm = { activityType: 'Note', subject: '', body: '', followUpOn: '', followUpTime: '' };
        this.loadActivities(true);
      },
      error: (e: any) => {
        this.loggingActivity = false;
        this.activityError = e?.error?.message ?? e?.error?.error ?? 'Failed to log activity.';
      },
    });
  }

  deleteActivity(a: CrmActivityRow): void {
    if (!confirm('Delete this logged item? This cannot be undone.')) return;
    this.http.delete(`/api/activities/${a.id}`).pipe(takeUntil(this.destroyed$)).subscribe({
      next: () => {
        this.snackBar.open('Item deleted', undefined, { duration: 2000 });
        this.loadActivities(true);
      },
      error: (e: any) => this.snackBar.open(e?.error?.message ?? 'Failed to delete item', 'OK', { duration: 4000 }),
    });
  }

  /** Combine a follow-up date with its optional 'HH:mm:ss' time into one Date for display. */
  followUpDateTime(a: CrmActivityRow): Date | null {
    if (!a.followUpOn) return null;
    const d = new Date(a.followUpOn.slice(0, 10) + 'T' + (a.followUpTime ? a.followUpTime.slice(0, 5) : '00:00') + ':00');
    return isNaN(d.getTime()) ? null : d;
  }

  completeFollowUp(a: CrmActivityRow): void {
    const note = (prompt('Add a note about how this follow-up was resolved (optional):') ?? '').trim();
    this.http.post(`/api/follow-ups/${a.id}/done`, { note: note || null })
      .pipe(takeUntil(this.destroyed$)).subscribe({
        next: () => { this.snackBar.open('Follow-up completed', undefined, { duration: 2000 }); this.loadActivities(true); },
        error: (e: any) => this.snackBar.open(e?.error?.message ?? 'Failed to complete follow-up', 'OK', { duration: 4000 }),
      });
  }

  activityIcon(type: string): string {
    const map: Record<string, string> = {
      Call: 'call', Email: 'email', Meeting: 'groups', Demo: 'slideshow',
      SiteVisit: 'place', Note: 'sticky_note_2', StageChange: 'timeline',
      CaseOpened: 'confirmation_number', CaseClosed: 'task_alt',
    };
    return map[type] ?? 'notes';
  }
}
